import React from "react";
import { Grid } from "@material-ui/core";
import { useTranslate } from "react-admin";
import { useFuelEconomyBaseMetrics } from "../hooks/useFuelEconomyHooks";
import type { FuelEconomyHookParams } from "../hooks/useFuelEconomyHooks";
import ReportCardWidget from "./ReportCardWidget";
import { round } from "./chartUtils";

export interface FuelEconomyBaseMetricsWidgetProps
  extends FuelEconomyHookParams {}

export const FuelEconomyBaseMetricsWidget: React.FC<FuelEconomyBaseMetricsWidgetProps> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const baseMetrics = useFuelEconomyBaseMetrics({
    fromDate,
    toDate,
    periodType,
  });

  const metrics: Array<{
    labelKey: string;
    unit: string;
    value: string | null;
    stops?: number[];
    isGrowing?: boolean;
    growth?: number;
    isReverse?: boolean;
    isLoading?: boolean;
    isError?: boolean;
  }> = [
    {
      labelKey: "fuelEconomyReport.averageConsumption",
      unit: "mpg",
      value:
        baseMetrics.average.data?.value != null
          ? round(baseMetrics.average.data.value)
          : null,
      stops: baseMetrics.average.data?.stops ?? undefined,
      isGrowing: baseMetrics.average.data?.isGrowing,
      growth: baseMetrics.average.data?.growth,
      isLoading: baseMetrics.average.loading,
      isError: !!baseMetrics.average.error,
    },
    {
      labelKey: "fuelEconomyReport.idleTimeConsumption",
      unit: "gal",
      value:
        baseMetrics.idle.data?.value != null
          ? round(baseMetrics.idle.data.value)
          : null,
      stops: baseMetrics.idle.data?.stops ?? undefined,
      isGrowing: baseMetrics.idle.data?.isGrowing,
      growth: baseMetrics.idle.data?.growth,
      isReverse: true,
      isLoading: baseMetrics.idle.loading,
      isError: !!baseMetrics.idle.error,
    },
    {
      labelKey: "fuelEconomyReport.speedingConsumption",
      unit: "mpg",
      value:
        baseMetrics.speeding.data?.value != null
          ? round(baseMetrics.speeding.data.value)
          : null,
      stops: baseMetrics.speeding.data?.stops ?? undefined,
      isGrowing: baseMetrics.speeding.data?.isGrowing,
      growth: baseMetrics.speeding.data?.growth,
      isLoading: baseMetrics.speeding.loading,
      isError: !!baseMetrics.speeding.error,
    },
    {
      labelKey: "fuelEconomyReport.maxFuelConsumption",
      unit: "mpg",
      value:
        baseMetrics.max.data?.value != null
          ? round(baseMetrics.max.data.value)
          : null,
      stops: baseMetrics.max.data?.stops ?? undefined,
      isGrowing: baseMetrics.max.data?.isGrowing,
      growth: baseMetrics.max.data?.growth,
      isLoading: baseMetrics.max.loading,
      isError: !!baseMetrics.max.error,
    },
    {
      labelKey: "fuelEconomyReport.minFuelConsumption",
      unit: "mpg",
      value:
        baseMetrics.min.data?.value != null
          ? round(baseMetrics.min.data.value)
          : null,
      stops: baseMetrics.min.data?.stops ?? undefined,
      isGrowing: baseMetrics.min.data?.isGrowing,
      growth: baseMetrics.min.data?.growth,
      isLoading: baseMetrics.min.loading,
      isError: !!baseMetrics.min.error,
    },
    {
      labelKey: "fuelEconomyReport.accelerateConsumption",
      unit: "mpg",
      value:
        baseMetrics.accelerate.data?.value != null
          ? round(baseMetrics.accelerate.data.value)
          : null,
      stops: baseMetrics.accelerate.data?.stops ?? undefined,
      isGrowing: baseMetrics.accelerate.data?.isGrowing,
      growth: baseMetrics.accelerate.data?.growth,
      isLoading: baseMetrics.accelerate.loading,
      isError: !!baseMetrics.accelerate.error,
    },
  ];

  return (
    <Grid container spacing={2} style={{ height: "100%" }}>
      {metrics.map((m) => (
        <Grid item xs={12} sm={6} key={m.labelKey} style={{ display: "flex" }}>
          <ReportCardWidget
            title={translate(m.labelKey, { _: m.labelKey })}
            value={m.value ?? "-"}
            unit={m.unit}
            stops={m.stops ?? undefined}
            isGrowing={m.isGrowing}
            growth={m.growth}
            isReverse={m.isReverse ?? false}
            isLoading={m.isLoading}
            isError={m.isError}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default FuelEconomyBaseMetricsWidget;
